import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import axios from 'axios';

import { StoreOrder, LineItem } from '../SharedTypes/Models';

const API_URL = '/api/orderbook/';


//-------------------------------------------- Store actions --------------------------------------------

export function useGetStoreOrders(locationId: number, date: string) {
	const queryClient = useQueryClient();

	return useQuery(
		['orders', { locationId, date }],
		async () => {
			const request = `${API_URL}store-order/${locationId}/${date}`;

			return await axios<{ result: StoreOrder[] }>(request);
		},
		{
			select: ({ data }) => {

				queryClient.invalidateQueries({
					queryKey: ['store-order'],
				});

				if (!data) {
					return [];
				}

				const result = data.result.map((storeOrder) => {
					return {
						...storeOrder,
					};
				});
				return result;
			},
		}
	);
}

export function useCreateStoreOrder() {
	const queryClient = useQueryClient();
	// Add isSaving state
	const [isSaving, setIsSaving] = useState(false);
	
	const mutation = useMutation({
		
		mutationFn: (storeorder: StoreOrder | null) =>
			axios.post<StoreOrder>(`${API_URL}store-order/create`, {
				...storeorder,
			}),
		
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['storeorders'],
			});
			queryClient.invalidateQueries({
				queryKey: ['storeorder'],
			});
			queryClient.invalidateQueries({
				queryKey: ['store-order'],
			});
			queryClient.invalidateQueries({
				queryKey: ['orders'],
			});

			// use artificial delay to show the saving state
			setTimeout(() => {setIsSaving(false)}, 1000);

      return true;
		},

		onError: (error) => {
			setIsSaving(false);
			return false;
		}
	});

	const createStoreOrder = async (order: StoreOrder | null) => {
    setIsSaving(true);
		try {
			const response = await mutation.mutateAsync(order);
			return response;
		} catch (error) {
			throw error;
		} 
  };

	return { createStoreOrder, isSaving };

}

export function useCancelStoreOrder() {
	const queryClient = useQueryClient();
	// Add isSaving state
	const [isCancelling, setIsCancelling] = useState(false);
	
	const mutation = useMutation({
		
		mutationFn: (orderNumber: string,) =>
			axios.post(`${API_URL}cancel/store-order/${orderNumber}`, {
			}),
		
		onSuccess: () => {

			queryClient.invalidateQueries({
				queryKey: ['cancel'],
			});

			// use artificial delay to show the saving state
			setTimeout(() => setIsCancelling(false), 750);
			return true;
		},

		onError: (error) => {
			setIsCancelling(false);
			return false;
		}
	});

	const cancelStoreOrder = async (orderNumber: string) => {
		setIsCancelling(true);
		try {
			const response = await mutation.mutateAsync(orderNumber);
			return response;
		} catch (error) {
			throw error;
		} finally {
			setIsCancelling(false);
		}
	};

	return { cancelStoreOrder, isCancelling };

}

//-------------------------------------------- PRODUCTION --------------------------------------------

export function useGetPlanableOrders() {
	const queryClient = useQueryClient();

	return useQuery(
		['planner'],
		async () => {
			const request = `${API_URL}production/planable-orders`;

			return await axios<{ result: StoreOrder[] }>(request);
		},
		{
			select: ({ data }) => {

				queryClient.invalidateQueries({
					queryKey: ['planable-orders'],
				});

				queryClient.invalidateQueries({
					queryKey: ['production/planable-orders'],
				});

				if (!data) {
					return [];
				}

				const result = data.result.map((planableOrders) => {
					return {
						...planableOrders,
					};
				});
				return result;
			},
			refetchOnWindowFocus: false,
			staleTime: 0,
			cacheTime: 0,
		},
	);
}

export function useGetProductionOrders(date: string) {
	const queryClient = useQueryClient();

	return useQuery(
		['productionorders', { date }],
		async () => {
			const request = `${API_URL}production/production-orders/${date}`;

			return await axios<{ result: StoreOrder[] }>(request);
		},
		{
			select: ({ data }) => {

				queryClient.invalidateQueries({
					queryKey: ['production-orders'],
				});

				queryClient.invalidateQueries({
					queryKey: ['production/production-orders'],
				});

				if (!data) {
					return [];
				}

				const result = data.result.map((productionOrders) => {
					return {
						...productionOrders,
					};
				});
				return result;
			},
		},
	);
}

export function useApproveLineItem() {
	const queryClient = useQueryClient();
	const [isApproving, setIsApproving] = useState(false);
	
	const mutation = useMutation({
		mutationFn: (lineItem: LineItem) =>
			axios.post<StoreOrder>(`${API_URL}production/approve`, {
				...lineItem,
			}),
		
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['storeorders'],
			});
			
			setIsApproving(false)

			return true;
		},

		onError: (error) => {
			setIsApproving(false);
			return false;
		}
	});

	const approveLineItem = async (lineItem: LineItem) => {
		// Set isSaving before making request
        setIsApproving(true);

		try {
			const response = await mutation.mutateAsync(lineItem);
			return response;
		} catch (error) {
			throw error;
		} finally {
			setIsApproving(false);
		}
    };

	return { approveLineItem, isApproving };

}

export function usePrepareLineItem() {
	const queryClient = useQueryClient();
	const [isPreparing, setIsPreparing] = useState(false);
	
	const mutation = useMutation({
		mutationFn: (lineItem: LineItem) =>
			axios.post<StoreOrder>(`${API_URL}production/prepare`, {
				...lineItem,
			}),
		
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['production/prepared'],
			});
			
			setIsPreparing(false)

			return true;
		},

		onError: (error) => {
			setIsPreparing(false);
			return false;
		}
	});

	const prepareLineItem = async (lineItem: LineItem) => {
		// Set isSaving before making request
        setIsPreparing(true);

		try {
			const response = await mutation.mutateAsync(lineItem);
			return response;
		} catch (error) {
			throw error;
		} finally {
      setIsPreparing(false);
		}
    };

	return { prepareLineItem, isPreparing };
}

export function useCraftLogger() {
	const queryClient = useQueryClient();

	return useMutation({
    mutationFn: async ({ type, message }: { type: string; message: string }) => {
      const request = `${API_URL}/log/log-${type}?message=${encodeURIComponent(message)}`;
      const { data } = await axios<{ result: boolean }>(request);
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['log-' + variables.type],
      });
    },
  });
}